import useTranslation from "next-translate/useTranslation";
import React from "react";
import { CalculatorBodyText, CalculatorHeadlineLeft } from "src/components/Calculator/CalculatorTypography";
import { CalculatorImage } from "src/components/Calculator/CalculatorImage";
import { makeStyles } from "@material-ui/core/styles";
import { TheCycleverseTheme } from "src/theme";
import CalculatorTable from "./CalculatorTable";
import Calculator from "./Calculator";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { CalculatorButtonLinkCentered } from "../components/CalculatorButtonLinkCentered";
import { CalculatorButton } from "../components/CalculatorButton";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  scrollDownBtnContainer: {
    textAlign: "center",
    marginTop: "30px",
    marginBottom: "30px",
    cursor: "pointer",
  },

  button: {
    backgroundColor: theme.buttonBackgroundColor,
    color: theme.palette.primary.main,
    width: "225px",
  },

  centeredCalculatorButton: {
    backgroundColor: theme.buttonBackgroundColor,
    color: theme.palette.primary.main,
    textAlign: "center",
  },

  paragraphWithDifferentSyles: {
    fontSize: "16px",
    fontWeight: 300,
    fontFamily: theme.typography.fontFamily,
    lineHeight: "24px",
  },
  orangeBoldText: {
    color: theme.calculatorOrangeBoldColor,
    fontWeight: 600,
  },

  underline: {
    textDecoration: "underline",
    cursor: "pointer",
    color: theme.buttonBackgroundColor,
  },

  bold: {
    fontWeight: 600,
  },

  calculatorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "50px",
    flexWrap: "wrap",
    "@media (max-width: 451px)": {
      flexDirection: "column",
      gap: "10px",
    },
  },

  blueTextColor: {
    color: theme.blueTextColor,
  },

  searchDivider: {
    height: 1,
    background: theme.highlightGradient,
    marginTop: "50px",
  },

  horizontalLine: {
    borderBottom: theme.grayBorderColor,
    marginTop: "50px",
  },
}));

const tableHeaders = ["tableHeader1", "tableHeader2", "tableHeader3", "tableHeader4"];
const tableRows = [
  { id: "1", cells: ["row1Cell1", "row1Cell2", "row1Cell3", "row1Cell4"] },
  { id: "2", cells: ["row2Cell1", "row2Cell2", "row2Cell3", "row2Cell4"] },
  { id: "3", cells: ["row3Cell1", "row3Cell2", "row3Cell3", "row3Cell4"] },
];

const LumenLuxRechnerCalculator = () => {
  const classes = useStyles();
  const { t } = useTranslation("lumenLuxRechner");

  const scrollToCalculator = () => {
    const element = document.getElementById("below-calculator");
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        top: element.offsetTop,
      });
    }, 100);
  };

  const scrollToTheTop = () => {
    const element = document.getElementById("calculator");
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        top: element.offsetTop,
      });
    }, 100);
  };

  const isSmallDevice = useMediaQuery("(max-width: 585px)");

  return (
    <>
      <CalculatorImage
        src="/images/calculators/lumen-lux-rechner/Grafischer-Vergleich-Lumen-und-Lux.svg"
        width={isSmallDevice ? 450 : 750}
        height={isSmallDevice ? 150 : 350}
        layout="intrinsic"
        alt={t("altTextImage1")}
      />

      <div style={{ marginTop: "50px" }} className={classes.paragraphWithDifferentSyles}>
        <div>
          <span>{t("text1a")}</span>
          <span className={classes.orangeBoldText}>{t("textInOrange1")}</span>
          <span>{t("text1b")}</span>
        </div>
        <div>
          <span>{t("text1c")}</span>
          <span className={classes.orangeBoldText}>{t("textInOrange2")}</span>
          <span>{t("text1d")}</span>
        </div>
      </div>

      <CalculatorButton text={t("scrollDownButton")} scroll idToScrollTo="calculator" />

      <CalculatorHeadlineLeft>{t("overline")}</CalculatorHeadlineLeft>
      <CalculatorBodyText>{t("paragraph1")}</CalculatorBodyText>

      <div className={classes.paragraphWithDifferentSyles}>
        <span onClick={scrollToCalculator} className={classes.underline}>
          {t("scrollToCalculator")}
        </span>
        <span>{` ${t("paragraph2")}`}</span>
      </div>
      <div className={classes.horizontalLine} />
      <div id="calculator">
        <CalculatorHeadlineLeft>{t("calculatorOverline")}</CalculatorHeadlineLeft>
      </div>

      <CalculatorBodyText>{t("paragraph3")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph4")}</CalculatorBodyText>

      <Calculator />

      <div className={classes.searchDivider} />
      <div id="below-calculator">
        <CalculatorHeadlineLeft>{t("paragraph21")}</CalculatorHeadlineLeft>
      </div>

      <CalculatorBodyText>{t("paragraph5")}</CalculatorBodyText>

      <div className={classes.paragraphWithDifferentSyles}>
        <span className={classes.bold}>{t("theProblemBold")}</span>
        <span>{t("paragraph6")}</span>
      </div>

      <div style={{ marginTop: "30px" }}>
        <CalculatorImage
          src="/images/calculators/lumen-lux-rechner/Lumen-Candela-Lux-Grafik.svg"
          width={isSmallDevice ? 450 : 750}
          height={isSmallDevice ? 250 : 350}
          layout="intrinsic"
          alt={t("altTextImage2")}
        />
      </div>

      <CalculatorHeadlineLeft>{t("overline2")}</CalculatorHeadlineLeft>
      <CalculatorBodyText>{t("paragraph7")}</CalculatorBodyText>
      <CalculatorHeadlineLeft>{t("overline3")}</CalculatorHeadlineLeft>
      <CalculatorBodyText>{t("paragraph8")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph9")}</CalculatorBodyText>
      <CalculatorBodyText>{t("text2")}</CalculatorBodyText>

      <div className={`${classes.paragraphWithDifferentSyles} ${classes.orangeBoldText}`}>{t("formula")}</div>
      <CalculatorHeadlineLeft>{t("paragraph20")}</CalculatorHeadlineLeft>
      <CalculatorBodyText>{t("paragraph10")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph11")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph12")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph13")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph14")}</CalculatorBodyText>
      <div className={`${classes.paragraphWithDifferentSyles} ${classes.orangeBoldText}`}>{t("formula1")}</div>

      <CalculatorBodyText>{t("text3")}</CalculatorBodyText>
      <div className={`${classes.paragraphWithDifferentSyles} ${classes.orangeBoldText}`}>{t("formula2")}</div>

      <CalculatorHeadlineLeft>{t("paragraph15")}</CalculatorHeadlineLeft>

      <CalculatorTable rows={tableRows} headers={tableHeaders} />
      <CalculatorBodyText>{t("paragraph16")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph17")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph18")}</CalculatorBodyText>

      <CalculatorImage
        src="/images/calculators/lumen-lux-rechner/Vergleich-Lumen-Lux.svg"
        width={isSmallDevice ? 450 : 750}
        height={isSmallDevice ? 150 : 350}
        layout="intrinsic"
        alt={t("altTextImage3")}
      />
      <div className={classes.paragraphWithDifferentSyles}>
        <span>{t("paragraph19a")}</span>
        <span onClick={scrollToTheTop} style={{ cursor: "pointer" }} className={classes.blueTextColor}>{` ${t(
          "blueText1"
        )} `}</span>
        <span>{t("paragraph19b")}</span>
      </div>

      <CalculatorHeadlineLeft>{t("overline4")}</CalculatorHeadlineLeft>
      <CalculatorButtonLinkCentered text={t("lastButtonLink")} href="/beleuchtung" />
    </>
  );
};

export default LumenLuxRechnerCalculator;
